import React, { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { useQueryClient } from "@tanstack/react-query";

import { useTheme } from "@/context/Theme.context";
import { Button, FormRow, Select, ShowWhen } from "@/components/common";
import PaymentTable from "./payment-table";
import PaymentMethods from "./payment-methods";
import { useParcelsRequest } from "@/api/useParcelsRequest";
import ParcelStatusSelectFlow, {
  parcelStatusOptions,
} from "@/components/common/select/parcel-status-select/parcel-status-select";

import {
  useFinalPayShipmentProcessRequest,
  useGetPaymentUrlRequest,
  usePaymentProvidersDataRequest,
  useStartPayShipmentProcessRequest,
  useGetAmountToPayShipments,
} from "@/api/usePaymentsRequests";
import { ParcelStatus, PaymentProvider, Theme } from "@/enums";
import { IShipment } from "@/types/apiResponse.types";
import { useGetBalanceInfoRequest } from "@/api/useGetBalanceInfoRequest";
import { formatNumber } from "@/utils/formatNumber";

interface TPaymentForm {
  hideShipmentType?: boolean;
  closeModal?: () => void;
}

const PaymentForm = ({ hideShipmentType, closeModal }: TPaymentForm) => {
  const queryClient = useQueryClient();

  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [parcelStatus, setParcelStatus] = useState("");
  const [selectedMethodId, setSelectedMethodId] = useState<
    PaymentProvider | ""
  >("");
  // const [isPayByBalance, setIsPayByBalance] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const theme = useTheme();

  const t = useTranslations();

  const { data, isLoading } = useParcelsRequest({
    isPaid: false,
  });

  const { data: balanceData } = useGetBalanceInfoRequest();

  const balanceValue =
    theme === Theme.UZ
      ? balanceData?.data?.data?.balanceNational
      : balanceData?.data?.data?.balanceUsd;

  const unpaidParcelsData = useMemo<{
    unpaidParcels: IShipment[];
    totalAmount: number;
  }>(() => {
    // additional status to filter unpaid parcels
    const statusses = [
      parcelStatus,
      ParcelStatus.COMMON_STATUS_RECEIVED_OUTSTOCK,
    ];

    const parcelsData = data?.data?.data;

    if (!parcelsData)
      return {
        unpaidParcels: [],
        totalAmount: 0,
      };

    let unpaidParcels: IShipment[] = [];

    if (!parcelStatus) {
      unpaidParcels = parcelsData.filter((parcel) =>
        parcelStatusOptions.some(
          (option) => option.id === parcel.commonStatusCode,
        ),
      );
    } else {
      unpaidParcels = parcelsData.filter((parcel) =>
        statusses.includes(parcel.commonStatusCode),
      );
    }

    let priceField = theme === Theme.UZ ? "priceNational" : "priceUsd";

    if (theme === Theme.UK) {
      priceField = "priceUsd";
    }

    return {
      unpaidParcels,
      totalAmount: selectedOrderIds?.reduce((acc, id) => {
        const parcel = unpaidParcels.find((item) => item.shipmentId === id);
        return acc + (parcel?.[priceField] || 0);
      }, 0),
    };
  }, [data, parcelStatus, selectedOrderIds]);

  // differenece between balance and unpaid parcels amount
  const balanceDiff = useMemo(() => {
    const diff = balanceValue - unpaidParcelsData.totalAmount;
    const shouldPayByBank = balanceValue > 0 && diff >= 0 ? false : true;

    return {
      shouldPayByBank,
      amount: diff,
      bankAmount: shouldPayByBank ? parseFloat(Math.abs(diff).toFixed(2)) : 0,
    };
  }, [balanceValue, unpaidParcelsData.totalAmount]);

  const { data: paymentProvidersData } = usePaymentProvidersDataRequest(
    balanceDiff.shouldPayByBank,
  );

  const { data: paymentUrlData } = useGetPaymentUrlRequest({
    totalAmount: balanceDiff.bankAmount,
    selectedMethodId,
    enabled: balanceDiff.shouldPayByBank,
  });

  const { data: getAmountToPayShipmentsData, isLoading: isAmountToPayLoading } =
    useGetAmountToPayShipments({
      shipmentIds: selectedOrderIds,
    });

  const { mutateAsync: startPayShipmentProcess } =
    useStartPayShipmentProcessRequest();

  const { mutateAsync: finalPayShipmentProcess } =
    useFinalPayShipmentProcessRequest();

  const providersList = paymentProvidersData?.data?.data || [];

  const paymentUrl = paymentUrlData?.data?.data;

  const amountToPayShipments = getAmountToPayShipmentsData?.data?.data;

  const handleSelectAllOrder = () => {
    if (selectedOrderIds.length < unpaidParcelsData?.unpaidParcels?.length) {
      setSelectedOrderIds(
        unpaidParcelsData?.unpaidParcels?.map((parcel) => parcel.shipmentId) ||
          [],
      );
    } else {
      setSelectedOrderIds([]);
    }
  };

  const handleSelectOrder = (id: number) => {
    if (!selectedOrderIds.includes(id)) {
      setSelectedOrderIds([...selectedOrderIds, id]);
    } else {
      setSelectedOrderIds(
        selectedOrderIds.filter((selectedId) => selectedId !== id),
      );
    }
  };

  const handleSelectMethod = (id: PaymentProvider) => {
    setSelectedMethodId(id);
    // setIsPayByBalance(false);
  };

  const handleTogglePayByBalance = () => {
    // setIsPayByBalance((prev) => !prev);
    setSelectedMethodId("");
  };

  const handlePayByBank = async () => {
    setIsSubmitting(true);
    try {
      if (!!selectedMethodId && !!paymentUrl) {
        await startPayShipmentProcess(selectedOrderIds);
        window.open(paymentUrl.urlForPay, "_self");
        return;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayByBalance = async () => {
    setIsSubmitting(true);
    try {
      await startPayShipmentProcess(selectedOrderIds);
      await finalPayShipmentProcess();
      await queryClient.invalidateQueries();
      if (closeModal) {
        closeModal();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (balanceDiff.shouldPayByBank) {
      handlePayByBank();
    } else {
      handlePayByBalance();
    }
  };

  let toPayAmount = amountToPayShipments?.totalPriceForNotPayedInNational;
  if (theme === Theme.UK) {
    toPayAmount = amountToPayShipments?.totalPriceForNotPayedInUsd;
  }

  let commisionAmount = amountToPayShipments?.commissionAmountInNational;
  if (theme === Theme.UK) {
    commisionAmount = amountToPayShipments?.commissionAmountInUsd;
  }

  return (
    // <div className="w-full xl:w-[70vw]">
    <div className="w-full">
      <ShowWhen condition={!hideShipmentType}>
        <FormRow className="mb-4 w-full max-w-[240px] pl-[35px]">
          <Select
            label=""
            selectedValue={parcelStatusOptions.find(
              (option) => option.id === parcelStatus,
            )}
            className="!pt-0"
            name="parcelStatus"
          >
            <ParcelStatusSelectFlow
              onSelect={(selectedValue: any) => {
                setParcelStatus(selectedValue.id);
                setSelectedOrderIds([]);
                setSelectedMethodId("");
              }}
            />
          </Select>
        </FormRow>
      </ShowWhen>
      <PaymentTable
        parcelsData={unpaidParcelsData?.unpaidParcels}
        isLoading={isLoading}
        selectedIds={selectedOrderIds}
        handleSelect={handleSelectOrder}
        handleSelectAll={handleSelectAllOrder}
      />
      <ShowWhen
        condition={balanceDiff.shouldPayByBank && balanceDiff.bankAmount > 0}
      >
        <div className="px-[35px]">
          <PaymentMethods
            showSelectBalance
            // isPayByBalance={isPayByBalance}
            handleTogglePayByBalance={handleTogglePayByBalance}
            providersList={providersList}
            totalAmount={balanceDiff.bankAmount}
            selectedMethodId={selectedMethodId}
            handleSelectMethod={handleSelectMethod}
          />
        </div>
      </ShowWhen>
      <div className="border-1 my-[24px] border-b border-[#f3f3f3]" />
      <div className="flex flex-col items-center justify-between px-[35px] pb-[24px]">
        <Button
          disabled={
            !amountToPayShipments ||
            isSubmitting ||
            isAmountToPayLoading ||
            (balanceDiff.shouldPayByBank && !selectedMethodId)
          }
          onClick={handleSubmit}
        >
          <span className="text-sm !font-medium text-white">
            {t("pay")} ({formatNumber(toPayAmount?.toFixed(2) || 0)}{" "}
            {t(
              theme === Theme.UZ
                ? "uzs"
                : theme === Theme.GEORGIA
                  ? "gel"
                  : "$",
            )}
            )
          </span>
        </Button>
        <ShowWhen condition={balanceDiff.shouldPayByBank}>
          <p
            className={`mt-[10px] text-sm font-normal ${theme !== Theme.UK ? "!text-[#0e7A89]" : ""}`}
          >
            {t("bank_commission")}:{" "}
            {isAmountToPayLoading
              ? ""
              : formatNumber(commisionAmount?.toFixed(2) || 0)}
          </p>
        </ShowWhen>
      </div>
    </div>
  );
};

export default PaymentForm;
